import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import AgentToolLoadingRow from './AgentToolLoadingRow';
import {
  getStatusWords,
  detectUiLocale,
  familyFromSearchType
} from '../../utils/searchStatusWords';
import { getToolDescription } from '../../utils/toolDisplayConfig';

/**
 * Resolves a single loading strip's frozen tool label + themed word family, then renders the
 * shared AgentToolLoadingRow. One component for both paths so they cannot drift (TD-2341):
 *   - mode='searching' (TD-2334 async company search): the search kind's "<kind>_poll" label and
 *     its themed word family (characteristics vs comparables).
 *   - mode='tool' (every other agent tool): the live tool's label and the neutral generic words.
 *
 * The label is frozen once per mount (lazy useState) so the randomized variant never reshuffles
 * on the ~1 Hz elapsed re-render; the caller remounts per search/tool via `key`.
 */
const AgentToolLoadingStrip = ({
  mode = 'tool',
  searchType = '',
  toolName = '',
  startTime = null
}) => {
  const isSearch = mode === 'searching';
  const family = isSearch ? familyFromSearchType(searchType) : 'generic';
  const labelKey = isSearch ? `${searchType || 'characteristics_search'}_poll` : toolName || '';

  const words = useMemo(() => getStatusWords(detectUiLocale(), family), [family]);
  const [toolLabel] = useState(() => getToolDescription(labelKey));

  return (
    <AgentToolLoadingRow
      startTime={startTime}
      toolLabel={toolLabel}
      words={words}
      srLabel={isSearch ? 'Searching…' : 'Working…'}
    />
  );
};

AgentToolLoadingStrip.propTypes = {
  /** 'searching' for the async company searches, 'tool' for any other agent tool. */
  mode: PropTypes.oneOf(['searching', 'tool']),
  /** Search kind (e.g. 'characteristics_search'); picks the poll label + themed word family. */
  searchType: PropTypes.string,
  /** Raw live tool name (tool mode); labels the grey box. */
  toolName: PropTypes.string,
  /** Epoch ms when the search/tool started; drives the elapsed timer. */
  startTime: PropTypes.number
};

export default AgentToolLoadingStrip;
